import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/sonner';
import axios from 'axios';
import {
  UserPlus,
  User,
  Phone,
  Mail,
  Building2,
  Home,
  MapPin,
  MessageSquare,
  CheckCircle,
  Loader2,
  ArrowLeft,
  Share2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Origen del cliente options
const ORIGEN_OPTIONS = [
  { value: 'referido', label: 'Referido' },
  { value: 'puerta_fria', label: 'Puerta Fría' },
  { value: 'web', label: 'Web' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'meta_ads', label: 'Meta Ads' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'llamada', label: 'Llamada' },
  { value: 'otros', label: 'Otros' },
];

// Form field component
function FormField({ icon: Icon, label, required, children, error }) {
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-zinc-400 font-['Manrope']">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

export default function NuevoContacto() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdContact, setCreatedContact] = useState(null);
  const [errors, setErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    tipo_cliente: 'hogar', // Default to hogar
    nombre_contacto: '',
    telefono: '',
    email: '',
    empresa: '',
    direccion: '',
    ciudad: '',
    codigo_postal: '',
    observaciones: '',
    origen_cliente: 'otros',
    referido_por: '',
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombre_contacto.trim()) {
      newErrors.nombre_contacto = 'El nombre es obligatorio';
    }
    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es obligatorio';
    }
    
    // Email format if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email no válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, completa los campos obligatorios');
      return;
    }

    setIsSubmitting(true);

    try {
      const contactData = {
        tipo_cliente: formData.tipo_cliente, // Required: "hogar" or "empresa"
        nombre_contacto: formData.nombre_contacto.trim(),
        telefono: formData.telefono.trim(),
        email: formData.email || null,
        empresa: formData.tipo_cliente === 'empresa' ? formData.empresa : null,
        direccion: formData.direccion || null,
        codigo_postal: formData.codigo_postal || null,
        ciudad: formData.ciudad || null,
        observaciones: formData.observaciones || null,
        origen_cliente: formData.origen_cliente,
        referido_por: formData.referido_por || null,
      };

      const response = await axios.post(`${API_URL}/api/comercial/contactos`, contactData, {
        withCredentials: true
      });

      setCreatedContact(response.data);
      setSuccess(true);
      toast.success('Contacto creado correctamente');

    } catch (err) {
      console.error('Error creating contact:', err);
      const errorMsg = err.response?.data?.detail || 'Error al crear el contacto';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSuccess(false);
    setCreatedContact(null);
    setFormData({
      tipo_cliente: 'hogar',
      nombre_contacto: '',
      telefono: '',
      email: '',
      empresa: '',
      direccion: '',
      ciudad: '',
      codigo_postal: '',
      observaciones: '',
      origen_cliente: 'otros',
      referido_por: '',
    });
  };

  const isEmpresa = formData.tipo_cliente === 'empresa';

  // Success state
  if (success) {
    return (
      <ComercialLayout
        title="Nuevo Contacto"
        subtitle="Crear oportunidad comercial"
        onLogout={handleLogout}
      >
        <div className="max-w-lg mx-auto mt-12 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-light text-white font-['Outfit'] mb-2">
            ¡Contacto creado!
          </h2>
          <p className="text-zinc-400 mb-2">
            El contacto se ha añadido a tu CRM en estado &quot;Contacto Nuevo&quot;
          </p>
          {createdContact && (
            <p className="text-sm text-sky-400 mb-6">
              Código: {createdContact.client_code}
            </p>
          )}
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              onClick={resetForm}
              className="border-white/20 text-zinc-300 hover:text-white"
              data-testid="crear-otro-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Crear otro contacto
            </Button>
            <Button
              onClick={() => navigate('/comercial/crm')}
              className="bg-sky-500 hover:bg-sky-600 text-white"
              data-testid="ver-crm-btn"
            >
              Ver en CRM
            </Button>
          </div>
        </div>
      </ComercialLayout>
    );
  }

  return (
    <ComercialLayout
      title="Nuevo Contacto"
      subtitle="Crear oportunidad comercial rápida"
      onLogout={handleLogout}
      headerActions={
        <Button
          variant="outline"
          onClick={() => navigate('/comercial/crm')}
          className="border-white/20 text-zinc-300 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al CRM
        </Button>
      }
    >
      <div className="max-w-2xl mx-auto">
        {/* Info banner */}
        <div className="mb-6 p-4 rounded-xl bg-sky-500/[0.08] border border-sky-500/20">
          <div className="flex items-center gap-3">
            <UserPlus className="w-5 h-5 text-sky-400 flex-shrink-0" />
            <p className="text-sm text-zinc-300 font-['Manrope']">
              Crea un contacto rápido con los datos mínimos. Podrás completar la información 
              más tarde desde la ficha del cliente.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tipo de Cliente */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-zinc-500" />
              Tipo de Cliente
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleChange('tipo_cliente', 'hogar')}
                className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                  !isEmpresa 
                    ? 'bg-sky-500/20 border-sky-500/50 text-sky-300' 
                    : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:border-white/20'
                }`}
                data-testid="tipo-hogar"
              >
                <Home className={`w-6 h-6 ${!isEmpresa ? 'text-sky-400' : 'text-zinc-500'}`} />
                <div className="text-left">
                  <p className={`font-medium ${!isEmpresa ? 'text-sky-300' : 'text-white'}`}>Hogar</p>
                  <p className="text-xs text-zinc-500">Cliente particular</p>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => handleChange('tipo_cliente', 'empresa')}
                className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                  isEmpresa 
                    ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' 
                    : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:border-white/20'
                }`}
                data-testid="tipo-empresa"
              >
                <Building2 className={`w-6 h-6 ${isEmpresa ? 'text-amber-400' : 'text-zinc-500'}`} />
                <div className="text-left">
                  <p className={`font-medium ${isEmpresa ? 'text-amber-300' : 'text-white'}`}>Empresa</p>
                  <p className="text-xs text-zinc-500">Negocio o empresa</p>
                </div>
              </button>
            </div>
          </div>

          {/* Datos básicos */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-zinc-500" />
              Datos Básicos
            </h3>
            <div className="space-y-4">
              {isEmpresa && (
                <FormField icon={Building2} label="Nombre de la empresa">
                  <Input
                    value={formData.empresa}
                    onChange={(e) => handleChange('empresa', e.target.value)}
                    placeholder="Empresa S.L."
                    className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 focus:border-amber-500/50"
                    data-testid="contacto-empresa"
                  />
                </FormField>
              )}

              <FormField icon={User} label={isEmpresa ? "Persona de contacto" : "Nombre completo"} required error={errors.nombre_contacto}>
                <Input
                  value={formData.nombre_contacto}
                  onChange={(e) => handleChange('nombre_contacto', e.target.value)}
                  placeholder="Nombre y apellidos"
                  className={`bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 ${isEmpresa ? 'focus:border-amber-500/50' : 'focus:border-sky-500/50'}`}
                  data-testid="contacto-nombre"
                />
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField icon={Phone} label="Teléfono" required error={errors.telefono}>
                  <Input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="+34 600 000 000"
                    className={`bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 ${isEmpresa ? 'focus:border-amber-500/50' : 'focus:border-sky-500/50'}`}
                    data-testid="contacto-telefono"
                  />
                </FormField>

                <FormField icon={Mail} label="Email" error={errors.email}>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@ejemplo.com"
                    className={`bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 ${isEmpresa ? 'focus:border-amber-500/50' : 'focus:border-sky-500/50'}`}
                    data-testid="contacto-email"
                  />
                </FormField>
              </div>
            </div>
          </div>

          {/* Ubicación (opcional) */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-zinc-500" />
              Ubicación <span className="text-xs text-zinc-500 font-normal">(opcional)</span>
            </h3>
            <div className="space-y-4">
              <FormField icon={MapPin} label="Dirección">
                <Input
                  value={formData.direccion}
                  onChange={(e) => handleChange('direccion', e.target.value)}
                  placeholder="Calle, número, piso..."
                  className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/20"
                  data-testid="contacto-direccion"
                />
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Código Postal">
                  <Input
                    value={formData.codigo_postal}
                    onChange={(e) => handleChange('codigo_postal', e.target.value)}
                    placeholder="28001"
                    maxLength={5}
                    className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/20"
                    data-testid="contacto-cp"
                  />
                </FormField>

                <FormField label="Ciudad">
                  <Input
                    value={formData.ciudad}
                    onChange={(e) => handleChange('ciudad', e.target.value)}
                    placeholder="Madrid"
                    className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 focus:border-white/20"
                    data-testid="contacto-ciudad"
                  />
                </FormField>
              </div>
            </div>
          </div>

          {/* Origen */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
              <Share2 className="w-4 h-4 text-zinc-500" />
              Origen del Contacto
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {ORIGEN_OPTIONS.map(origen => {
                const isSelected = formData.origen_cliente === origen.value;
                return (
                  <button
                    key={origen.value}
                    type="button"
                    onClick={() => handleChange('origen_cliente', origen.value)}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      isSelected 
                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300' 
                        : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:border-white/20'
                    }`}
                    data-testid={`origen-${origen.value}`}
                  >
                    <span className="text-xs font-medium">{origen.label}</span>
                  </button>
                );
              })}
            </div>

            {formData.origen_cliente === 'referido' && (
              <div className="mt-4">
                <FormField icon={User} label="¿Quién lo refirió?">
                  <Input
                    value={formData.referido_por}
                    onChange={(e) => handleChange('referido_por', e.target.value)}
                    placeholder="Nombre del cliente que lo refirió"
                    className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500/50"
                    data-testid="contacto-referido-por"
                  />
                </FormField>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-zinc-500" />
              Observaciones <span className="text-xs text-zinc-500 font-normal">(opcional)</span>
            </h3>
            <textarea
              value={formData.observaciones}
              onChange={(e) => handleChange('observaciones', e.target.value)}
              placeholder="Notas sobre el contacto, horarios de contacto, intereses..."
              rows={3}
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 text-white placeholder:text-zinc-600 focus:border-white/20 focus:outline-none resize-none font-['Manrope']"
              data-testid="contacto-observaciones"
            />
          </div>

          {/* Submit */}
          <div className="flex items-center justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/comercial')}
              className="border-white/20 text-zinc-400 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className={`${isEmpresa ? 'bg-amber-500 hover:bg-amber-600' : 'bg-sky-500 hover:bg-sky-600'} text-white min-w-[180px]`}
              data-testid="crear-contacto-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Crear Contacto
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </ComercialLayout>
  );
}
